import { type FestivalPricingInput } from './calculatePrice.tsx'

/**
 * Per-concert pricing (the 2026 `PER_CONCERT` model).
 *
 * A ticket's price is its concert's price for the ticket's type. `null` means
 * the type is not set / not offered and is never coerced to 0; `0` means free.
 * There are no tiers, no pass cap and no savings.
 */

/**
 * The ticket types, in the order they are offered: the three "pris etter
 * evne" prices the buyer picks between themselves, highest first, then
 * children.
 */
export const TICKET_TYPES = ['generous', 'adult', 'student', 'child'] as const

export type TicketType = (typeof TICKET_TYPES)[number]

/** The "pris etter evne" types (all but children), in the same order. */
export const PAY_WHAT_YOU_CAN_TYPES = TICKET_TYPES.filter(
	(type): type is Exclude<TicketType, 'child'> => type !== 'child',
)

/**
 * What a type is called. The stored values predate the labels: `adult` was
 * "Voksen" and `student` "Student" until 2026 moved to pay-what-you-can
 * prices, and stored tickets keep their values.
 */
export const TICKET_TYPE_LABEL: Record<TicketType, string> = {
	adult: 'Ordinær',
	student: 'Lavpris',
	generous: 'Generøs',
	child: 'Barn',
}

/** The type's label, or the raw value for a type this code doesn't know. */
export const ticketTypeLabel = (type: string) =>
	TICKET_TYPE_LABEL[type as TicketType] ?? type

export const isTicketType = (value: unknown): value is TicketType =>
	(TICKET_TYPES as ReadonlyArray<unknown>).includes(value)

/** The four nullable prices carried by an event (mirrors `Event` in Prisma). */
export type EventPrices = {
	adultPrice: number | null
	studentPrice: number | null
	generousPrice: number | null
	childPrice: number | null
}

/** The `Event` column holding each type's price. */
export const TYPE_PRICE_FIELD: Record<TicketType, keyof EventPrices> = {
	adult: 'adultPrice',
	student: 'studentPrice',
	generous: 'generousPrice',
	child: 'childPrice',
}

/** An event's prices with absent fields as unset (`null`). */
export const pricesOf = (event: Partial<EventPrices>): EventPrices => ({
	adultPrice: event.adultPrice ?? null,
	studentPrice: event.studentPrice ?? null,
	generousPrice: event.generousPrice ?? null,
	childPrice: event.childPrice ?? null,
})

/** The price fields every price read selects from `Event`. */
export const EVENT_PRICE_SELECT = {
	adultPrice: true,
	studentPrice: true,
	generousPrice: true,
	childPrice: true,
} as const

/**
 * The concert's price for one ticket type: a number (0 = free) or `null` when
 * the type is not set and therefore not offered.
 */
export function eventPrice(
	event: EventPrices,
	type: TicketType,
): number | null {
	const field = TYPE_PRICE_FIELD[type]
	return field ? event[field] : null
}

/**
 * The exact sum of the per-ticket concert prices. Unset types contribute 0 to
 * the sum (they are not offered), but `null` is never treated as a *free* type
 * by the UI — see `eventPrice`.
 */
export function priceForTickets(
	tickets: Array<{ event: EventPrices; type: string }>,
): number {
	return tickets.reduce((sum, ticket) => {
		const price = eventPrice(ticket.event, ticket.type as TicketType)
		return sum + (price ?? 0)
	}, 0)
}

/** Every price unset → the concert is "Billetter kommer" (TBA). */
export function hasNoPrices(event: EventPrices): boolean {
	return TICKET_TYPES.every((type) => eventPrice(event, type) == null)
}

/**
 * At least one price is set and none of the set prices is non-zero → the
 * concert is free. All-unset is TBA, not free.
 */
export function isFreeEvent(event: EventPrices): boolean {
	if (hasNoPrices(event)) return false
	return TICKET_TYPES.every((type) => !eventPrice(event, type))
}

/**
 * The concert offers more than one paying price ("pris etter evne"): the
 * buyer picks the one that suits them, and nobody checks at the door.
 */
export function isPayWhatYouCan(event: EventPrices): boolean {
	const paid = TICKET_TYPES.map((type) => eventPrice(event, type)).filter(
		(price): price is number => price != null && price > 0,
	)
	return new Set(paid).size > 1
}

/**
 * Parse a nullable integer price from a form entry: blank or absent → `null`
 * (unset), otherwise the parsed integer (0 = free). Non-numeric → `null`.
 */
export function parsePriceInput(
	value: FormDataEntryValue | null,
): number | null {
	if (typeof value !== 'string' || value.trim() === '') return null
	const parsed = Number.parseInt(value, 10)
	return Number.isNaN(parsed) ? null : parsed
}

/**
 * An event form's price inputs, parsed with `parsePriceInput`. `null` when the
 * form has none of them: only PER_CONCERT events render the price inputs, so
 * other models leave the prices untouched.
 */
export function parsePriceForm(formData: {
	get(name: string): FormDataEntryValue | null
}): EventPrices | null {
	const raw = TICKET_TYPES.map((type) => formData.get(TYPE_PRICE_FIELD[type]))
	if (raw.every((value) => value === null)) return null
	return Object.fromEntries(
		TICKET_TYPES.map((type, i) => [
			TYPE_PRICE_FIELD[type],
			parsePriceInput(raw[i] ?? null),
		]),
	) as EventPrices
}

/**
 * A festival's PER_CONCERT default prices as event prices, to prefill a new
 * concert (copy-on-create, never a read-time fallback).
 */
export const festivalDefaultPrices = (
	festival: {
		defaultAdultPrice?: number | null
		defaultStudentPrice?: number | null
		defaultGenerousPrice?: number | null
		defaultChildPrice?: number | null
	} | null,
): EventPrices => ({
	adultPrice: festival?.defaultAdultPrice ?? null,
	studentPrice: festival?.defaultStudentPrice ?? null,
	generousPrice: festival?.defaultGenerousPrice ?? null,
	childPrice: festival?.defaultChildPrice ?? null,
})

/** Whether any parsed price is negative (0 = free is fine; null = unset). */
export function hasNegativePrice(
	...prices: Array<number | null | undefined>
): boolean {
	return prices.some((price) => price != null && price < 0)
}

/**
 * What one ticket costs right now: its snapshot wins; otherwise the concert's
 * current price for its type (PER_CONCERT only). Legacy TIERED/SIMPLE tickets
 * without a snapshot cannot be priced per ticket — they resolve to 0 here and
 * are priced per person through the legacy `payAllQuote` path instead.
 */
export function ticketPrice(
	ticket: { priceAtPurchase: number | null; type: string },
	event: EventPrices,
	pricingModel?: string | null,
): number {
	if (ticket.priceAtPurchase != null) return ticket.priceAtPurchase
	if (pricingModel === 'PER_CONCERT') {
		return eventPrice(event, ticket.type as TicketType) ?? 0
	}
	return 0
}

/**
 * No tickets on sale for a concert yet ("Billetter kommer"): the concert has
 * no prices, or its festival isn't PER_CONCERT. Only PER_CONCERT festivals
 * sell through the cart — legacy TIERED/SIMPLE price per person over named
 * tickets, so an unnamed cart ticket could never be priced or paid there
 * (ADR 0003).
 */
export function arePricesPending(
	event: Partial<EventPrices> & {
		festival?: FestivalPricingInput | null
	},
): boolean {
	if (event.festival?.pricingModel !== 'PER_CONCERT') return true
	return hasNoPrices(pricesOf(event))
}
